<template>
  <div :class="['gam-simple-input', containerClasses, { 'is-small': isSmall, 'is-medium': isMedium }]">
    <label v-if="!isPlaceholder || isFocussed">{{ label ? localize(label) : '' }}</label>
    <input
      ref="input"
      v-bind="$attrs"
      :value="modelValue"
      :type="type"
      :min="min"
      @input="onChange"
      @focus="() => (isFocussed = true)"
      @blur="handleBlur"
    />
  </div>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { isSafari } from '@/core/helpers/ui.helper';
import { computed, ref, useAttrs } from 'vue';
import type { GamInputValueType } from '../composables/constants/components/gamInput.constants';

defineOptions({
  inheritAttrs: false,
});

interface Props {
  label?: string;
  modelValue?: string | number | null;
  isSmall?: boolean;
  isMedium?: boolean;
  isLowerCase?: boolean;
  isUsername?: boolean;
  type?: GamInputValueType;
  min?: number;
}

const props = defineProps<Props>();
useAttrs();

const input = ref<HTMLInputElement>();
const inputValue = ref<string>(props.modelValue ? String(props.modelValue) : '');
const isFocussed = ref<boolean>(false);

const emit = defineEmits(['update:modelValue', 'blur']);

const isPlaceholder = computed(
  () =>
    !(
      (typeof props.modelValue === 'string' && String(props.modelValue).length > 0) ||
      (typeof props.modelValue === 'number' && !isNaN(props.modelValue))
    ),
);

const containerClasses = computed(() => ({
  'is-focussed': isFocussed.value,
  'is-safari': isSafari.value,
  'is-placeholder': isPlaceholder.value,
}));

const handleBlur = () => {
  isFocussed.value = false;
  emit('blur');
};

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement;

  let value = target.value;
  inputValue.value = value;

  if (props.type === 'number') {
    value = value.replace(/\D/g, '');
    value = parseInt(value, 10).toString();
  }

  if (props.isLowerCase) {
    value = value.toLocaleLowerCase();
  }

  if (props.isUsername) {
    value = value.toLocaleLowerCase().replace(/[^a-z0-9_\.]/g, '');
  }

  target.value = value;

  if (props.type === 'number') {
    emit('update:modelValue', parseInt(value, 10));
  } else {
    emit('update:modelValue', value);
  }
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-simple-input {
  border: 1px solid var(--color-white-20);
  border-radius: 25px;
  min-height: 47px;
  position: relative;
  transition: border-color 200ms;
  width: 100%;

  @include hover {
    border-color: var(--color-pink);
  }

  label {
    position: absolute;
    top: 5px;
    left: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--color-white-30);
  }

  input {
    border: none;
    outline: none;
    background: transparent;
    color: var(--color-white);
    border-radius: 25px;
    position: absolute;
    width: 100%;
    height: 100%;
    padding-left: 20px;
    padding-top: 12px;
    box-sizing: border-box;

    &::placeholder {
      color: var(--color-white-50);
    }

    &:focus::placeholder {
      opacity: 0;
    }
  }

  &.is-placeholder {
    input {
      padding-top: 0;
    }
    input:focus {
      padding-top: 12px;
    }
  }
}
</style>
